import type { Article } from '~/composables/useArchives';
import { getCollectionNavTree } from '~/composables/useContentRoute';
import { detectNavMode } from '~/utils/navTree';
import { slugToCollection, getLocaleSlug, hasSiteInLocale } from '~/utils/siteLocale';
import { contentPathToUrl } from '~/utils/routeMapping';
import { findArchiveInLocale, resolveArticleLocaleSwitch } from '~/composables/useArchives';
import { querySettings } from '~/utils/queries/settings';
import { queryPages } from '~/utils/contentQueries';
import { parseArchiveSubpath } from '~/utils/archiveRoute';
import { parseLocaleSwitchContext, extractSiteChildren, type LocaleSwitchContext } from '~/utils/localeSwitch';
import { toast } from '@econnect/ui-core';

/**
 * Resolve archive routes (article, index, paginated, category/tag-filtered).
 * Returns null when the URL isn't archive-shaped or we're inside /docs/.
 */
async function resolveArchiveSwitch(ctx: LocaleSwitchContext): Promise<string | null> {
  // Archive routing is only valid outside /docs/.
  if (ctx.inDocs) return null;

  const { currentLocale, newLocale, site, parts } = ctx;

  const settings = await querySettings(currentLocale);
  const archive = settings?.archives.find(a => a.path === site);
  if (!archive) return null;

  // parts: ['', locale, site, ...subpath] — slice(3) yields the archive subpath
  // segments that the shared parser knows how to decode.
  const sub = parseArchiveSubpath(parts.slice(3));

  // Article? — try to resolve the peer in the target locale; on miss, fall
  // through to the archive base below.
  if (sub?.kind === 'article') {
    const articlePath = `/${currentLocale}/${site}/${sub.slug}`;
    const current = await (await queryPages('articles')).path(articlePath).first() as Article | null;
    if (current) {
      const target = await resolveArticleLocaleSwitch(current, newLocale);
      if (target) return target;
    }
  }

  // Archive index / paginated / filtered — preserve category|tag filter when the
  // target locale also declares that slug; always drop pagination across
  // locale boundaries (page numbers don't survive translation). `null` from
  // the parser (unrecognised subpath) also falls through to the archive base.
  const targetArchive = await findArchiveInLocale(archive.translationKey, newLocale);
  if (!targetArchive) return `/${newLocale}`;
  const base = `/${newLocale}/${targetArchive.path}`;
  if (sub?.kind === 'filtered') {
    const list = sub.filterKind === 'category' ? targetArchive.categories : targetArchive.tags;
    if (list.some(e => e.slug === sub.filterSlug)) {
      return `${base}/${sub.filterKind}/${sub.filterSlug}`;
    }
  }
  return base;
}

/**
 * Resolve commercial routes via translationKey (sections + commercial collections).
 * Docs sites use `resolveByTranslationKey` (per-site collections); commercial
 * section hubs/pages are not in `slugToCollection`, so they need this path.
 */
async function resolveCommercialByTranslationKey(
  ctx: LocaleSwitchContext,
): Promise<string | null> {
  if (ctx.inDocs) return null;

  const { currentPath, newLocale } = ctx;
  const current =
    (await (await queryPages('sections')).path(currentPath).first()) ??
    (await (await queryPages('commercial')).path(currentPath).first());
  const translationKey = (current as { translationKey?: string } | null)?.translationKey;
  if (!translationKey) return null;

  for (const col of ['sections', 'commercial'] as const) {
    const matches = await (await queryPages(col))
      .where('translationKey', '=', translationKey)
      .all();
    const peer = matches.find((p: any) => p.path.startsWith(`/${newLocale}/`));
    if (peer?.path) return peer.path;
  }
  return null;
}

/**
 * Resolve via translationKey — if the current page declares one, find its
 * sibling in the target locale (same collection first, then per-locale-slug
 * fallback). Returns null when no key or no peer is found.
 */
async function resolveByTranslationKey(ctx: LocaleSwitchContext): Promise<string | null> {
  const { currentPath, newLocale, site, inDocs } = ctx;
  const col = await slugToCollection(site);
  if (!col) return null; // `site` isn't a known docs site → no collection to search

  const currentPage = await (await queryPages(col)).path(currentPath).first();
  const translationKey = currentPage?.translationKey;
  if (!translationKey) return null;

  // Search current collection first (works when one collection spans multiple locales)
  const allMatches = await (await queryPages(col))
    .where('translationKey', '=', translationKey)
    .all();
  const target = allMatches.find((p: any) => p.path.startsWith(`/${newLocale}/`));
  if (target) return inDocs ? contentPathToUrl(target.path) : target.path;

  // Fallback: collections are per-locale-slug — search each target locale collection.
  const allSections = await (await queryPages('sections')).select('stem').all();
  const targetSlugs: string[] = allSections
    .filter((m: any) => {
      const segs = ((m.stem ?? '') as string).split('/');
      return segs[0] === newLocale
        && (segs[1] === 'docs' || /^\d+\.docs$/.test(segs[1] ?? ''))
        && segs.length === 4
        && segs[3] === 'index';
    })
    .map((m: any) => (m.stem ?? '').split('/')[2]?.replace(/^\d+\./, '') ?? '')
    .filter(Boolean);
  for (const slug of targetSlugs) {
    const targetCol = await slugToCollection(slug);
    // Skip unknown slugs (no backing collection) and the one already searched.
    if (!targetCol || targetCol === col) continue;
    const matches = await (await queryPages(targetCol))
      .where('translationKey', '=', translationKey)
      .all();
    const match = matches.find((p: any) => p.path.startsWith(`/${newLocale}/`));
    if (match) return inDocs ? contentPathToUrl(match.path) : match.path;
  }

  return null;
}

/**
 * Resolve via icon-based nav matching. Always returns a string when reached
 * (falling back to the prefix-only URL when no icon match is found); typed as
 * `string | null` so it composes uniformly with the other resolvers.
 */
async function resolveByIcon(ctx: LocaleSwitchContext): Promise<string | null> {
  const { currentLocale, newLocale, site, seg3, seg4, inDocs } = ctx;
  const newSiteSlug = await getLocaleSlug(site, newLocale);
  const col = await slugToCollection(site);
  const prefix = inDocs ? `/${newLocale}/docs/${newSiteSlug}` : `/${newLocale}/${newSiteSlug}`;
  // `site` isn't a known docs site → no nav tree to icon-match; fall back to the
  // bare locale-prefixed path (this resolver's documented last resort).
  if (!col) return prefix;
  const newCol = await slugToCollection(newSiteSlug);

  const [fromNav, toNav] = await Promise.all([
    getCollectionNavTree(col),
    newCol && newCol !== col ? getCollectionNavTree(newCol) : Promise.resolve([]),
  ]);
  const fullNav = [...fromNav, ...toNav];
  const fromChildren = extractSiteChildren(fullNav, currentLocale, site);
  const toChildren = extractSiteChildren(fullNav, newLocale, newSiteSlug);
  const mode = detectNavMode(fromChildren);

  if (mode === 'flat') {
    // seg3 = category slug — match by icon
    const fromCat = fromChildren.find((c: any) => c.path.split('/').pop() === seg3);
    if (!fromCat?.icon) return prefix;
    const toCat = toChildren.find((c: any) => c.icon === fromCat.icon);
    return toCat
      ? `${prefix}/${toCat.path.split('/').pop()}`
      : prefix;
  } else {
    // seg3 = sub-site slug — match by icon
    const fromSubSite = fromChildren.find((c: any) => c.path.split('/').pop() === seg3);
    if (!fromSubSite?.icon) return prefix;
    const toSubSite = toChildren.find((c: any) => c.icon === fromSubSite.icon);
    if (!toSubSite) return prefix;
    const toSubSiteSlug = toSubSite.path.split('/').pop() ?? '';

    if (!seg4) return `${prefix}/${toSubSiteSlug}`;

    // seg4 = category slug within sub-site — match by icon
    const fromCategories = fromSubSite.children?.filter((c: any) => c.path !== fromSubSite.path) ?? [];
    const toCategories = toSubSite.children?.filter((c: any) => c.path !== toSubSite.path) ?? [];
    const fromCat = fromCategories.find((c: any) => c.path.split('/').pop() === seg4);
    if (!fromCat?.icon) return `${prefix}/${toSubSiteSlug}`;
    const toCat = toCategories.find((c: any) => c.icon === fromCat.icon);
    return toCat
      ? `${prefix}/${toSubSiteSlug}/${toCat.path.split('/').pop()}`
      : `${prefix}/${toSubSiteSlug}`;
  }
}

/**
 * Result of resolving a locale switch.
 *
 * `matched: true`  — we found a confident peer for the exact page (a shared
 *                    `translationKey`, an archive/article peer, or the page IS
 *                    a site/locale root whose target-locale twin is itself).
 *                    Navigate silently.
 * `matched: false` — no declared peer for this specific page. `path` is the
 *                    CLOSEST ANCESTOR that actually exists in the target locale
 *                    (a translated site/subsite/category root, or the locale
 *                    home when even the site is absent). The caller navigates
 *                    there AND shows a toast — never a silent guess at the leaf,
 *                    which is what produced wrong-page landings / 404s.
 */
export interface LocaleSwitchResult {
  path: string;
  matched: boolean;
}

/**
 * Resolves the target path when switching locale.
 *
 * Accepts URL paths (/{locale}/docs/{site}/...) — converts internally.
 * Returns URL paths with /docs/ prefix when the input was a docs URL.
 *
 * Resolution order:
 * 1. Locale/site root → the root is its own peer (matched).
 * 2. Archive/article routing (when not in /docs/) — short-circuits (matched).
 * 3. Commercial translationKey peer lookup (when not in /docs/) — before docs-only site maps
 *    (oplossingen≠solutions; without this → wrong slug / dropped child).
 * 4. Site absent in target locale → locale home, closest existing parent (unmatched).
 * 5. Docs translationKey — the current page's declared sibling in the target locale (matched).
 * 6. No declared peer → closest existing ancestor via icon nav-walk (unmatched → toast).
 */
export async function resolveLocaleSwitchPath(
  currentUrlPath: string,
  newLocale: string,
): Promise<LocaleSwitchResult> {
  const ctx = parseLocaleSwitchContext(currentUrlPath, newLocale);

  // Switching from a locale root: the target locale root is the exact peer.
  if (!ctx.site) return { path: `/${ctx.newLocale}`, matched: true };

  // Archive resolution must run first — it short-circuits before any docs-specific logic.
  const archiveResult = await resolveArchiveSwitch(ctx);
  if (archiveResult !== null) return { path: archiveResult, matched: true };

  // Commercial hubs use localized slugs (oplossingen/solutions/…). Docs-only
  // `hasSiteInLocale` / `getLocaleSlug` maps miss them and used to drop the child
  // path → /en/oplossingen 404. Resolve by translationKey before that fallback.
  if (!ctx.inDocs) {
    const commercialPeer = await resolveCommercialByTranslationKey(ctx);
    if (commercialPeer) return { path: commercialPeer, matched: true };
  }

  const newSiteSlug = await getLocaleSlug(ctx.site, ctx.newLocale);

  // Site doesn't exist in the target locale at all → the deepest route that
  // DOES exist is the locale home. (Landing on `/{locale}/docs/{slug}` here
  // would 404 — the slug isn't there.) Closest parent, so: toast.
  if (!(await hasSiteInLocale(ctx.site, ctx.newLocale))) {
    return { path: `/${ctx.newLocale}`, matched: false };
  }

  const siteRoot = ctx.inDocs
    ? `/${ctx.newLocale}/docs/${newSiteSlug}`
    : `/${ctx.newLocale}/${newSiteSlug}`;

  // Switching from the site root itself → the target site root is the peer.
  if (!ctx.seg3) return { path: siteRoot, matched: true };

  // A shared translationKey is the only CONFIDENT peer for a specific page.
  const byKey = await resolveByTranslationKey(ctx);
  if (byKey) return { path: byKey, matched: true };

  // No declared peer. resolveByIcon walks the target-locale nav tree and
  // returns the deepest node that exists (matched subsite/category, else the
  // site root) — i.e. the closest existing ancestor. Never a silent leaf
  // guess: report it as unmatched so the caller toasts.
  const closest = await resolveByIcon(ctx);
  return { path: closest ?? siteRoot, matched: false };
}

/** Localized "we couldn't find this page, sent you to its parent" toast copy,
 * shown in the locale the user just switched TO. Keep in sync with the
 * supported-locale set (build/runtimeConfig discoverHomeLocales). */
const LOCALE_SWITCH_FALLBACK_MSG: Record<string, { title: string; desc: string }> = {
  nl: { title: "Pagina niet beschikbaar in deze taal", desc: "Je bent naar de dichtstbijzijnde pagina gebracht." },
  en: { title: "Page not available in this language", desc: "You've been taken to the closest page." },
  de: { title: "Seite in dieser Sprache nicht verfügbar", desc: "Sie wurden zur nächstgelegenen Seite geleitet." },
  fr: { title: "Page indisponible dans cette langue", desc: "Vous avez été redirigé vers la page la plus proche." },
  es: { title: "Página no disponible en este idioma", desc: "Te hemos llevado a la página más cercana." },
  it: { title: "Pagina non disponibile in questa lingua", desc: "Sei stato reindirizzato alla pagina più vicina." },
  pl: { title: "Strona niedostępna w tym języku", desc: "Przeniesiono Cię do najbliższej strony." },
  cs: { title: "Stránka není v tomto jazyce dostupná", desc: "Byli jste přesměrováni na nejbližší stránku." },
  sk: { title: "Stránka nie je v tomto jazyku dostupná", desc: "Boli ste presmerovaní na najbližšiu stránku." },
};

/**
 * Switch locale end-to-end: resolve → navigate → toast on a fallback landing.
 * Shared by the desktop (HeaderActions) and mobile (MobileMenuFooter) language
 * switchers so their behaviour can't drift. Client-only (navigateTo + toast).
 */
export async function switchLocaleAndNotify(
  currentUrlPath: string,
  newLocale: string,
): Promise<void> {
  const { path, matched } = await resolveLocaleSwitchPath(currentUrlPath, newLocale);
  await navigateTo(path);
  if (!matched) {
    const copy = LOCALE_SWITCH_FALLBACK_MSG[newLocale] ?? LOCALE_SWITCH_FALLBACK_MSG.en!;
    toast.info(copy.title, { description: copy.desc });
  }
}
